import {
  add,
  differenceInBusinessDays,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInMinutes,
  formatDistanceStrict,
  formatDistanceToNow,
  parseISO,
  set,
  sub,
} from 'date-fns';
import { format as tzFormat, toDate, utcToZonedTime } from 'date-fns-tz';

import { DATE_FORMATS, OPTIONS, TIMES, TWENTY_FOUR_HOUR_REGEX } from '@/constants';

/**
 * Format date time string
 * Note that this method will return the date in your local timezone
 * @param {string | Date | number } dateValue The date to format
 * @param {string} [dateFormat=DATE_FORMATS.YYYY_MM_DD] The format we want to use
 * @param {string} [timeZoneName] The timezone we want to convert to
 * @returns {string} Formatted date string
 * @see https://date-fns.org/v2.12.0/docs/Unicode-Tokens for the additional tokens
 */
export function formatDateTime(dateValue, dateFormat = DATE_FORMATS.YYYY_MM_DD, timeZoneName) {
  const date = normalizeDate(dateValue);
  const dateFnsFormat = convertMomentFormat(dateFormat); //TODO: Remove this when we migrate completely from moment
  const timeZone = timeZoneName ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return tzFormat(toTimeZone(date, timeZone), dateFnsFormat, { timeZone });
}

/**
 * Adds time to a date
 * @param {string | Date | number} date The date to modify, fallsback on today
 * @param {Object} timeObject An object describing how much time to add
 * @see https://date-fns.org/v2.13.0/docs/add#arguments
 * @returns {Date}
 */
export function addTime(dateValue, timeObject) {
  const date = normalizeDate(dateValue);

  return add(date, timeObject);
}

/**
 * Subtracts time from a date
 * @param {string | Date | number} date The date to modify, fallsback on today
 * @param {Object} timeObject An object describing how much time to subtract
 * @see https://date-fns.org/v2.13.0/docs/sub#arguments
 * @returns {Date}
 */
export function subtractTime(dateValue, timeObject) {
  const date = normalizeDate(dateValue);

  return sub(date, timeObject);
}

/**
 * Formats the time that has elapsed since a given date, as a string.
 * @param {string} dateValue The date to use in calculation
 * @returns {string}
 */
export function timeSince(dateValue, strict = false) {
  const date = normalizeDate(dateValue);
  const now = normalizeDate();

  return strict ? formatDistanceStrict(date, now) : formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Converts the Date to UTC
 * TODO: Avoid forcing time-zone; instead use the user's (localtime) or company's time
 * @param {string | Date | number} dateValue The value we want to convert to UTC
 * @returns {Date}
 */
export function toUTC(dateValue) {
  const date = normalizeDate(dateValue);

  return toDate(date, { timeZone: 'Etc/UTC' });
}

/**
 * Converts a date to a specified time
 * @param {string | Date | number} dateValue The date to modify
 * @param {Object} timeObject The time object describing the change to the date
 * @returns {string} The formatted date with the time set to the time object
 */
export function convertToTime(dateValue, timeObject) {
  const utcDate = toUTC(dateValue);

  return set(utcDate, timeObject);
}

/**
 * Normalize a date value into a Date object
 * This function will accept a string, a Date or a number and return Date representation of the value
 * @param {string | Date | number } dateValue The date to format
 * @returns {Date}
 */
export function normalizeDate(dateValue) {
  if (!dateValue) {
    return new Date();
  }

  if (typeof dateValue === 'string') {
    return parseISO(dateValue);
  }

  return dateValue;
}

/**
 * Convert a date to a specific timezone
 * @param { string | Date | number } dateValue The date to format
 * @param {string} timeZone (https://en.wikipedia.org/wiki/List_of_tz_database_time_zones) (constants/index.js TIMEZONES)
 * @returns
 */
export function toTimeZone(dateValue, timeZone) {
  const utcDate = toUTC(dateValue);

  return utcToZonedTime(utcDate, timeZone);
}

/**
 * This function converts momentjs-specific format tokens to their datefns equivalent
 * @param {string} format The format to convert
 * @returns {string}
 * @see https://date-fns.org/v2.9.0/docs/parse
 * @see https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @returns {string} The updated format string
 */
export function convertMomentFormat(format) {
  // Uppercase "A" is not supported in date-fns only lowercase "a", which outputs an uppercase "AM/PM"
  // Lowercase is supported in moment but it outputs a lowercase "am/pm"
  let dateFormat = format.replace(/A/, 'a');

  // Datefns uses lowercase "yyyy" for a 4-digit year
  // Using "useAdditionalDayOfYearTokens" or "useAdditionalWeekYearTokens" does not solve this issue
  // These tokens allow you to use "day of a year" and "local week-numbering year"
  // they don't make "YYYY" act as "yyyy", so we need to replace them with the actual format datefns uses
  dateFormat = dateFormat.replace(/YYYY/, 'yyyy');
  dateFormat = dateFormat.replace(/DD/, 'dd');

  return dateFormat;
}

/**
 * returns the difference in minutes between two dates.
 * @param {Date | number | string} dateLeft The later date
 * @param {Date | number | string} dateRight The earlier date
 * @returns {number}
 */
export function diffInMinutes(dateLeft, dateRight) {
  return differenceInMinutes(normalizeDate(dateLeft), normalizeDate(dateRight));
}

/**
 * Returns the number of calendar days between the given dates.
 * This means that the times are removed from the dates and then the difference in days is calculated.
 * @param {Date | number | string} dateLeft The later date
 * @param {Date | number | string} dateRight The earlier date
 * @returns {number}
 */
export function diffInCalendarDays(dateLeft, dateRight) {
  return differenceInCalendarDays(normalizeDate(dateLeft), normalizeDate(dateRight));
}

/**
 * Returns the number of Business days between the given dates.
 * This means that the times are removed from the dates and then the difference in days is calculated.
 * @param {Date | number | string} dateLeft The later date
 * @param {Date | number | string} dateRight The earlier date
 * @returns {Date | number}
 */
export function diffInBusinessDays(dateLeft, dateRight) {
  return differenceInBusinessDays(normalizeDate(dateLeft), normalizeDate(dateRight));
}

/**
 * Returns the number of calendar months between the given dates.
 * @param {Date | number | string} dateLeft The later date
 * @param {Date | number | string} dateRight The earlier date
 * @returns {Date | number}
 */
export function diffInCalendarMonths(dateLeft, dateRight) {
  return differenceInCalendarMonths(normalizeDate(dateLeft), normalizeDate(dateRight));
}

/**
 * @deprecated Do not use. Legacy only.
 * TODO: move to `legacy/`
 * Behave like date-fns/set but works on UTC values instead of localtime.
 */
function setUTC(date, options) {
  for (const key in options) {
    // eslint-disable-next-line no-prototype-builtins
    if (!options.hasOwnProperty(key)) {
      continue;
    }

    const value = options[key];

    switch (key) {
      case 'hours':
        date.setUTCHours(value);
        break;
      case 'minutes':
        date.setUTCMinutes(value);
        break;
      case 'seconds':
        date.setUTCSeconds(value);
        break;
      case 'milliseconds':
        date.setUTCMilliseconds(value);
        break;
    }
  }

  return date;
}

/**
 * @deprecated Do not use. Legacy only.
 * TODO: move to `legacy/`
 * This function takes a Date instance and converts it to UTC Noon (12:00), this conversion
 * is required so the DateTime fields can be "synced" for the same date.
 */
export function toUTCNoonISO(dateValue, useTimeZone = true) {
  let date = useTimeZone ? normalizeDate(dateValue) : new Date(dateValue);

  date = setUTC(date, TIMES.NOON);
  return date.toISOString();
}

/**
 * @deprecated can now use formatDateTime and pass timezone 'UTC'
 * Format UTC date time string
 * Note that this method will return the date in UTC
 * @param {string | Date | number } dateValue The date to format
 * @param {string} dateFormat The format we want to use
 * @returns {string} Formatted date string
 * @see https://date-fns.org/v2.12.0/docs/Unicode-Tokens for the additional tokens
 */
export function formatDateTimeUTC(dateValue, dateFormat = DATE_FORMATS.YYYY_MM_DD) {
  return formatDateTime(dateValue, dateFormat, 'UTC');
}

/**
 * Returns the time in AM/PM format
 * This means that the times are removed from the dates and then the difference in days is calculated.
 * @param {String} time The time that needs to be formatted: Ex.: 08:30:00
 * @returns {Object}
 */
export function formatTimeTwelveHour(time, option = OPTIONS.TWELVE_HOUR_TIME_PERIODS.AM) {
  if (!time) {
    return {
      value: '',
      option,
    };
  }

  const timeSplit = time.split(':');
  const hours = parseInt(timeSplit[0]);
  const hoursTwelveHourFormat = ((hours + 11) % 12) + 1;
  const timePeriod = hours >= 12 ? OPTIONS.TWELVE_HOUR_TIME_PERIODS.PM.name : OPTIONS.TWELVE_HOUR_TIME_PERIODS.AM.name;

  return {
    value: `${hoursTwelveHourFormat}:${timeSplit[1]}`,
    option: {
      id: timePeriod.toLowerCase(),
      name: timePeriod,
    },
  };
}

/**
 * Returns the time in 24h format
 * @param {String} time The time that needs to be formatted: Ex.: 20:30:00
 * @returns {Object}
 */
export const formatTimeTwentyFourHour = ({ time, timePeriod }) => {
  if (!time.length) {
    return '';
  }

  const timeSplit = time.split(':');
  const hours = parseInt(timeSplit[0]);
  let hoursTwentyFourHourFormat = timePeriod === OPTIONS.TWELVE_HOUR_TIME_PERIODS.AM.id ? hours : hours + 12;

  if (hours === 12) {
    hoursTwentyFourHourFormat -= 12;
  }

  return `${hoursTwentyFourHourFormat}:${timeSplit[1]}:00`;
};

export const isValidTimeFormat = (time) => TWENTY_FOUR_HOUR_REGEX.test(time);

export const parsedISODate = (date) =>
  typeof date === 'string' ? new Date(parseISO(date)) : new Date(parseISO(formatDateTime(date)));

export const createIntervalTimeOptions = () => {
  const options = [];

  for (let idx = 1; idx <= 24; idx++) {
    const hour = String(idx).padStart(2, '0');
    const minutes = ['00', '15', '30', '45'];

    minutes.map((min) => options.push(`${hour}:${min}:00`));
  }

  return options;
};
